import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, Search } from "@mui/icons-material";

const normalizeYearName = (name) => String(name || "").trim().toLowerCase().replace(/\s+/g, "");

const TopicTreeSelect = ({
  value = [],
  onChange,
  catalog = [],
  yearId = "",
  onYearChange,
  schoolYears = [],
  disabled = false,
  label = "Topics",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedParents, setExpandedParents] = useState({});
  const [searchText, setSearchText] = useState("");

  const selectedIds = useMemo(
    () => new Set((value || []).map((id) => Number(id))),
    [value]
  );

  const yearFilteredCatalog = useMemo(() => {
    if (!yearId) return catalog;
    return catalog.filter((t) => Number(t.school_year_id || 0) === Number(yearId));
  }, [catalog, yearId]);

  const grouped = useMemo(() => {
    const byId = new Map();
    yearFilteredCatalog.forEach((t) => byId.set(Number(t.id), t));
    const roots = [];
    const childrenByParent = {};

    yearFilteredCatalog.forEach((t) => {
      const parentId = t.parent_id != null ? Number(t.parent_id) : null;
      if (parentId && byId.has(parentId)) {
        if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
        childrenByParent[parentId].push(t);
      } else {
        roots.push(t);
      }
    });

    roots.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    Object.keys(childrenByParent).forEach((pid) => {
      childrenByParent[pid].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    });

    return { roots, childrenByParent };
  }, [yearFilteredCatalog]);

  const searchFilteredGrouped = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return grouped;

    const roots = [];
    const childrenByParent = {};
    grouped.roots.forEach((root) => {
      const rootName = String(root.name || "").toLowerCase();
      const rootLabel = String(root.label || "").toLowerCase();
      const kids = grouped.childrenByParent[Number(root.id)] || [];

      const rootMatched = rootName.includes(term) || rootLabel.includes(term);
      const matchedKids = kids.filter((k) => {
        const subName = String(k.name || "").toLowerCase();
        const subLabel = String(k.label || "").toLowerCase();
        return subName.includes(term) || subLabel.includes(term);
      });

      if (rootMatched || matchedKids.length > 0) {
        roots.push(root);
        childrenByParent[Number(root.id)] = rootMatched ? kids : matchedKids;
      }
    });

    return { roots, childrenByParent };
  }, [grouped, searchText]);

  const open = Boolean(anchorEl);
  const popoverId = open ? "topic-tree-select-popover" : undefined;

  const toggleParentExpanded = (parentId) => {
    setExpandedParents((prev) => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  const toggleSingle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.(Array.from(next));
  };

  const toggleParentWithChildren = (parent) => {
    const parentId = Number(parent.id);
    const children = grouped.childrenByParent[parentId] || [];
    const ids = [parentId, ...children.map((c) => Number(c.id))];
    const allSelected = ids.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    ids.forEach((id) => {
      if (allSelected) next.delete(id);
      else next.add(id);
    });
    onChange?.(Array.from(next));
  };

  const clearAll = () => onChange?.([]);

  const selectedCount = selectedIds.size;
  const selectedTopics = useMemo(() => {
    if (selectedIds.size === 0) return [];
    return catalog
      .filter((t) => selectedIds.has(Number(t.id)))
      .map((t) => ({ id: Number(t.id), label: t.label || t.name || `#${t.id}` }));
  }, [catalog, selectedIds]);
  const yearTabs = useMemo(() => {
    const yearByName = new Map(
      schoolYears.map((y) => [normalizeYearName(y.name), y])
    );
    const y5 = schoolYears.find((y) => normalizeYearName(y.name) === "year5");
    const y3 = yearByName.get("year3");
    const y4 = yearByName.get("year4");
    const y6 = yearByName.get("year6");
    return [
      { id: "", name: "All" },
      ...(y3 ? [{ id: String(y3.id), name: y3.name }] : []),
      ...(y4 ? [{ id: String(y4.id), name: y4.name }] : []),
      ...(y5 ? [{ id: String(y5.id), name: y5.name }] : []),
      ...(y6 ? [{ id: String(y6.id), name: y6.name }] : []),
    ];
  }, [schoolYears]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={disabled}
        sx={{ minHeight: 40, justifyContent: "flex-start", width: "100%", textTransform: "none", py: 0.5 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", width: "100%" }}>
          {selectedTopics.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{label}</Typography>
          ) : (
            <>
              {selectedTopics.slice(0, 4).map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  label={t.label}
                  onDelete={(e) => { e.stopPropagation(); toggleSingle(t.id); }}
                  onClick={(e) => e.stopPropagation()}
                />
              ))}
              {selectedTopics.length > 4 && (
                <Chip size="small" variant="outlined" label={`+${selectedTopics.length - 4} more`} />
              )}
            </>
          )}
        </Box>
      </Button>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { width: 420, maxWidth: "95vw" } }}
      >
        <Box sx={{ p: 1.5 }}>
          <Tabs
            value={String(yearId || "")}
            onChange={(_, next) => onYearChange?.(next)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36 }}
          >
            {yearTabs.map((y) => (
              <Tab key={String(y.id)} value={String(y.id)} label={y.name} sx={{ minHeight: 36, textTransform: "none" }} />
            ))}
          </Tabs>
          <TextField
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search topics or subtopics"
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <List dense sx={{ maxHeight: 420, overflowY: "auto", py: 0 }}>
          {searchFilteredGrouped.roots.map((topic) => {
            const topicId = Number(topic.id);
            const children = searchFilteredGrouped.childrenByParent[topicId] || [];
            const isExpanded = expandedParents[topicId] ?? true;
            const countText = topic.question_count != null ? ` (${topic.question_count})` : "";
            return (
              <Box key={topicId}>
                <ListItemButton onClick={() => (children.length ? toggleParentWithChildren(topic) : toggleSingle(topicId))}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(topicId)}
                    onChange={() => (children.length ? toggleParentWithChildren(topic) : toggleSingle(topicId))}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {topic.name}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {countText}
                        </Typography>
                      </Typography>
                    }
                  />
                  {children.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleParentExpanded(topicId);
                      }}
                    >
                      {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  )}
                </ListItemButton>
                {children.length > 0 && isExpanded && (
                  <Box sx={{ pl: 5, pb: 0.5 }}>
                    {children.map((sub) => {
                      const subId = Number(sub.id);
                      const subCountText = sub.question_count != null ? ` (${sub.question_count})` : "";
                      return (
                        <ListItemButton key={subId} sx={{ py: 0.25 }} onClick={() => toggleSingle(subId)}>
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(subId)}
                            onChange={() => toggleSingle(subId)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                {sub.name}
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {subCountText}
                                </Typography>
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
          {searchFilteredGrouped.roots.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No topics match your search.
              </Typography>
            </Box>
          )}
        </List>
        <Divider />
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button size="small" onClick={clearAll} sx={{ textTransform: "none" }}>
            Clear all
          </Button>
          <Button size="small" variant="contained" onClick={() => setAnchorEl(null)} sx={{ textTransform: "none" }}>
            Done
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default TopicTreeSelect;
